import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { TaxReturnService } from '../../../swaggerServices';
import { ProcessStatePollingService } from './process-state-polling.service';
import { takeUntil } from 'rxjs/operators';
import { ProcessStates } from '../types/processState';

declare var $: any;
const el = elementRef => $(elementRef.nativeElement);

@Injectable({
  providedIn: 'root'
})
export class RunCalcsService {
  private _isRunningCalcs = new BehaviorSubject<boolean>(false);
  isRunningCalcs = this._isRunningCalcs.asObservable();

  private _isReady = new BehaviorSubject<boolean>(false);
  isReady = this._isReady.asObservable();

  private unsubscribe: Subject<void> = new Subject();

  private _calcResultsSummary = new BehaviorSubject<string[]>([]);
  calcResultsSummary = this._calcResultsSummary.asObservable();
  
  private _calcFailures = new BehaviorSubject<string[]>([]);
  calcFailures = this._calcFailures.asObservable();
  
  private _calcDebug = new BehaviorSubject<string[]>([]);
  calcDebug = this._calcDebug.asObservable();
  
  private _showCalcDebug = new BehaviorSubject<boolean>(false);
  showCalcDebug = this._showCalcDebug.asObservable();
  

  constructor(private taxReturnService: TaxReturnService,
    private processStatePollingService: ProcessStatePollingService
  ) {}

  runCalcs(taxReturnId: number, calcResultsModal: ElementRef, isAdmin: boolean, runAfterRunCalcsSuccess: () => any) {
    this._isRunningCalcs.next(true);
    this.taxReturnService.runTaxReturnCalculations(taxReturnId).subscribe(result => {
      this.processStatePollingService.startPolling("runCalcs", "taxReturn", taxReturnId, 5000, 3);
      this.getCalcsResults(taxReturnId, calcResultsModal, isAdmin, runAfterRunCalcsSuccess);
    }, e => {
      this._isRunningCalcs.next(false);
    });
  }

  getCalcsResults(taxReturnId: number, calcResultsModal: ElementRef, isAdmin: boolean, runAfterRunCalcsSuccess: () => any) {
      this.processStatePollingService
        .subscribeData("runCalcs", "taxReturn", taxReturnId)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(result => {
  
          this.clearCalcsResults()
          // this.isRunningCalcs = false;
          if (!result || !result.state || !result.message) {
            this._calcResultsSummary.next(['Unable to get calcs status, system will retry or please re-run calcs again in few minutes.']);
            return;
          }
          this._calcResultsSummary.next(result.message.split("\n"));
  
          const elem = el(calcResultsModal);
          elem.modal('show');
          elem.find('.content')[0].scrollTop = 0;
          if (result.state === ProcessStates.CANCELED) {
            this.processStatePollingService.cancelPolling("runCalcs", "taxReturn", taxReturnId)
            this._isRunningCalcs.next(false);
            this._calcResultsSummary.next(['Calcs stopped responding and has been automatically cancelled']);
          }
          if (result.state === ProcessStates.FAILED) {
            this.processStatePollingService.cancelPolling("runCalcs", "taxReturn", taxReturnId)
            this._isRunningCalcs.next(false);
            this._calcResultsSummary.next(['Calcs has completed Running however it has failed to run successfully']);
          }
          if (result.state === ProcessStates.SUCCESS) {
            this.processStatePollingService.cancelPolling("runCalcs", "taxReturn", taxReturnId)
            this._isRunningCalcs.next(false);
            this._calcResultsSummary.next(['Calcs has completed running unsuccessfully']);
            let parsedResult;
            try {
              parsedResult = JSON.parse(result.message)
              const summary = [];
              const failures = [];
              const debugPlans = [];
              if (parsedResult.success) {
                if (parsedResult.comment) {
                  this._isRunningCalcs.next(false);
                  runAfterRunCalcsSuccess();
                }
                summary.push(`Successfully executed ${parsedResult.numSuccesses} plans`);
                if (parsedResult.failures && parsedResult.failures.length) {
                  summary.push(`Failed to execute ${parsedResult.failures.length} plans:`);
                  failures.push(...parsedResult.failures.map((f, i) => `${f}`));
                }
                if (isAdmin) {
                  if (parsedResult.comment) {
                    summary.push(...(parsedResult.comment.messages || []));
                  }
                  debugPlans.push(...(parsedResult.debug || []))
                }
              } else {
                summary.push(`Failed to run calculations: ${parsedResult.error}`);
              }
              this._calcResultsSummary.next(summary);
              this._calcFailures.next(failures);
              this._calcDebug.next(debugPlans);
            } catch (err) {
              this._calcResultsSummary.next([result.message]);
              this._calcFailures.next([]);
              this._calcDebug.next([]);
            }
          }
        }, e => {
          this.processStatePollingService.cancelPolling("runCalcs", "taxReturn", taxReturnId)
          this._isRunningCalcs.next(false);
        });
  }
  
  clearCalcsResults() {
    this._calcResultsSummary.next([]);
    this._calcFailures.next([]);
    this._showCalcDebug.next(false);
  }

  toggleCalcDebug() {
    this._showCalcDebug.next(!this._showCalcDebug.getValue());
  }

}